import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as Highcharts from 'highcharts';
import { AuthService } from 'src/app/service/auth.service';
import { RoutingService } from 'src/app/service/routing.service';
import { ScannerChartDataService } from './scanner-chart-data.service';
import { ScannerChartData, SCANNER_CHART_COLORS } from './scanner-chart.models';
import { QueryParams } from 'src/app/shared/models/query-params.interface';
import { SCANNER_REFRESH_PARAMS } from '../scanner.constants';

// Shared tooltip format for all series
const DEFAULT_TOOLTIP_FORMAT =
  '<tr><td style="color: {series.color}">{series.name}: </td>' +
  '<td style="text-align:right"><b>{point.y}</b></td></tr>';

@Component({
  selector: 'app-scanner-chart',
  templateUrl: './scanner-chart.component.html',
  styleUrls: ['./scanner-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScannerChartComponent implements OnInit, OnDestroy {
  chartData: Highcharts.SeriesOptionsType[] = [];
  isLoading = false;
  hasError = false;
  errorMessage = '';
  dualYAxis = true;
  yAxisTitle = 'Volume/ Event Counts';
  secondYAxisTitle = 'Score';
  scoreType = '';
  scoreLoading$: Observable<boolean> = this.chartDataService.scoreLoading$;

  private destroy$ = new Subject<void>();
  private previousParams: Params = {};

  tooltipOptions: Highcharts.TooltipOptions = {
    backgroundColor: 'rgba(44, 44, 44, 0.95)',
    borderColor: '#434343',
    borderRadius: 4,
    borderWidth: 1,
    style: { color: '#fff', fontSize: '14px' },
    shadow: false,
    followPointer: true,
    followTouchMove: true,
    shared: true,
    split: false,
    useHTML: true,
    headerFormat: '<small>{point.key}</small><table>',
    footerFormat: '</table>',
  };

  constructor(
    private route: ActivatedRoute,
    private auth: AuthService,
    private routing: RoutingService,
    private chartDataService: ScannerChartDataService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        if (
          this.routing.reFresh(
            params,
            this.previousParams,
            SCANNER_REFRESH_PARAMS as unknown as string[],
          )
        ) {
          this.loadChartData(params);
        }

        this.previousParams = { ...params };
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get defaultScoreType(): string {
    return this.auth.scorelabSettings.defaultScore;
  }

  private loadChartData(params: Params): void {
    this.isLoading = true;
    this.hasError = false;
    this.cdr.markForCheck();
    const filteredByEvents = !!(params.eventid || params.event_ids);
    this.chartDataService
      .fetchChartData(params as QueryParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          try {
            this.processChartData(data, filteredByEvents);
          } catch (error) {
            this.hasError = true;
            this.errorMessage = 'Error processing chart data.';
          } finally {
            this.isLoading = false;
            this.cdr.markForCheck();
          }
        },
        error: () => {
          this.hasError = true;
          this.errorMessage = 'Failed to load chart data.';
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private processChartData(
    data: ScannerChartData,
    filteredByEvents?: boolean,
  ): void {
    const hasScoreData = data.scoreData && data.scoreData.length > 0;
    const hasSentimentData =
      (data.scoreSentiLongData && data.scoreSentiLongData.length > 0) ||
      (data.scoreSentiShortData && data.scoreSentiShortData.length > 0);

    if (!hasScoreData && !hasSentimentData) {
      this.hasError = true;
      this.errorMessage = 'No data available for the selected period.';
      return;
    }

    const timestamps = data.dates.map((date) => new Date(date).getTime());

    const scoreInfo = this.auth.getScoreName();
    const scoreName = scoreInfo?.name || this.defaultScoreType;

    const isSentimentDataOnly = hasSentimentData && !hasScoreData;
    this.scoreType = isSentimentDataOnly ? 'sentiment' : this.defaultScoreType;
    this.secondYAxisTitle = isSentimentDataOnly ? 'Sentiment' : 'Score';

    this.chartData = [
      // Volume bar series (LEFT Y-axis)
      {
        type: 'column',
        name: 'Volume',
        data: data.volumeData.map((item, index) => [
          timestamps[index],
          item.volume,
        ]),
        color: SCANNER_CHART_COLORS.volume,
        yAxis: 0,
        zIndex: 1,
        tooltip: { pointFormat: DEFAULT_TOOLTIP_FORMAT, valueDecimals: 0 },
        maxPointWidth: 7,
        visible: !filteredByEvents,
      },
      // Events bar series (LEFT Y-axis)
      {
        type: 'column',
        name: 'Events',
        data: data.eventsData.map((item, index) => [
          timestamps[index],
          item.count,
        ]),
        color: SCANNER_CHART_COLORS.events,
        yAxis: 0,
        zIndex: 1,
        tooltip: { pointFormat: DEFAULT_TOOLTIP_FORMAT, valueDecimals: 0 },
      },
    ];

    // Add main score line if available
    if (hasScoreData) {
      this.chartData.push({
        type: 'line',
        name: scoreName,
        data: data.scoreData.map((item, index) => [
          timestamps[index],
          item.score,
        ]),
        color: SCANNER_CHART_COLORS.score,
        lineWidth: 2,
        yAxis: 1,
        zIndex: 3,
        tooltip: { pointFormat: DEFAULT_TOOLTIP_FORMAT, valueDecimals: 2 },
      });
    }

    // Add ESG sub-scores if available
    this.addEsgSubScores(data, timestamps);

    // Add sentiment lines if available
    this.addSentimentLines(data, timestamps);
  }

  private addEsgSubScores(data: ScannerChartData, timestamps: number[]): void {
    const esgSeries = [
      { data: data.scoreEData, name: 'E', color: SCANNER_CHART_COLORS.esgE },
      { data: data.scoreSData, name: 'S', color: SCANNER_CHART_COLORS.esgS },
      { data: data.scoreGData, name: 'G', color: SCANNER_CHART_COLORS.esgG },
    ];

    esgSeries.forEach((series) => {
      if (series.data?.length) {
        this.chartData.push({
          type: 'line',
          name: series.name,
          data: series.data.map((item, index) => [
            timestamps[index],
            item.score,
          ]),
          color: series.color,
          yAxis: 1,
          zIndex: 2,
          visible: false,
          tooltip: { pointFormat: DEFAULT_TOOLTIP_FORMAT, valueDecimals: 2 },
        });
      }
    });
  }

  private addSentimentLines(
    data: ScannerChartData,
    timestamps: number[],
  ): void {
    const sentimentSeries = [
      {
        data: data.scoreSentiLongData,
        name: data.sentimentLongLabel || 'Sentiment Long',
        color: SCANNER_CHART_COLORS.sentimentLong,
      },
      {
        data: data.scoreSentiShortData,
        name: data.sentimentShortLabel || 'Sentiment Short',
        color: SCANNER_CHART_COLORS.sentimentShort,
      },
    ];

    sentimentSeries.forEach((series) => {
      if (series.data?.length) {
        this.chartData.push({
          type: 'line',
          name: series.name,
          color: series.color,
          yAxis: 1,
          zIndex: 2,
          tooltip: {
            pointFormatter: function () {
              const value = Math.round((this.y || 0) * 100);
              return (
                '<tr><td style="color: ' +
                this.series.color +
                '">' +
                this.series.name +
                ': </td>' +
                '<td style="text-align:right"><b>' +
                value +
                '%</b></td></tr>'
              );
            },
          },
          data: series.data.map((item, index) => [
            timestamps[index],
            item.score,
          ]),
        });
      }
    });
  }
}
